import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import {
  Search,
  MoreVertical,
  Target,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useInitiatives } from "@/hooks/use-initiatives";
import { t } from "@/lib/i18n/messages";
import {
  getInitiativeStatus,
  getInitiativeType,
} from "@/lib/utils/initiative-helpers";
import { Input } from "@/components/ui/input";
import { ErrorMessage } from "@/components/ui/error-message";
import { SkeletonRow } from "@/components/ui/skeleton";
import type { Locale } from "@/types/auth";
import type {
  InitiativeCategory,
  InitiativeStatus,
  Initiative,
} from "@/types/initiative";
import { cn } from "@/lib/utils";

type ActionType = "fund" | "invite" | "reactivate";

interface InitiativesTableProps {
  locale: Locale;
  onAction?: (action: ActionType, initiative: Initiative) => void;
  initialStatus?: InitiativeStatus;
  refetchTrigger?: number;
}

const CATEGORIES: (InitiativeCategory | "all")[] = [
  "all",
  "community",
  "green",
];

const STATUSES: (InitiativeStatus | "all")[] = [
  "all",
  "draft",
  "pending_approval",
  "funded",
  "published",
  "active",
  "paused",
  "completed",
  "expired",
  "cancelled",
];

export function InitiativesTable({
  locale,
  onAction,
  initialStatus,
  refetchTrigger,
}: InitiativesTableProps) {
  const isRTL = locale === "ar";
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownStyle, setDropdownStyle] =
    useState<React.CSSProperties | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    data,
    loading,
    error,
    meta,
    refetch,
    setPage,
    setCategory,
    setStatus,
    setSearch,
    currentParams,
  } = useInitiatives({ initialPerPage: 10, initialStatus });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearch(debouncedSearch);
  }, [debouncedSearch, setSearch]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (refetchTrigger !== undefined) refetch();
  }, [refetchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenMenuId(null);
        setDropdownStyle(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(dateString));
  };

  const getLocalizedTitle = (initiative: Initiative) => {
    if (locale === "ar" && initiative.title_ar) return initiative.title_ar;
    if (locale === "en" && initiative.title_en) return initiative.title_en;
    return (
      initiative.title || initiative.title_en || initiative.title_ar || "—"
    );
  };

  const getAvailableActions = (
    initiative: Initiative,
  ): { key: ActionType; label: string }[] => {
    const actions: { key: ActionType; label: string }[] = [];
    if (initiative.status === "draft") {
      actions.push({ key: "fund", label: t(locale, "fundInitiative") });
    }
    if (initiative.status === "inactive") {
      actions.push({ key: "fund", label: t(locale, "setBudget") });
    }
    if (initiative.status === "active" || initiative.status === "published") {
      actions.push({ key: "invite", label: t(locale, "inviteParticipant") });
    }
    if (initiative.status === "inactive" || initiative.status === "expired") {
      actions.push({
        key: "reactivate",
        label: t(locale, "reactivateInitiative"),
      });
    }
    return actions;
  };

  const activeFilterCount =
    (currentParams.category ? 1 : 0) +
    (currentParams.status ? 1 : 0) +
    (currentParams.search ? 1 : 0);

  if (error && data.length === 0) {
    return (
      <ErrorMessage
        title={t(locale, "error")}
        message={error}
        onRetry={refetch}
        retryLabel={t(locale, "retry")}
      />
    );
  }

  const openInitiative = openMenuId
    ? data.find((d) => d.id === openMenuId)
    : null;
  const openActions = openInitiative ? getAvailableActions(openInitiative) : [];

  return (
    <>
      <div className="rounded-2xl border border-border bg-white dark:bg-card shadow-sm overflow-hidden">
        {/* ─── Filter row ──────────────────────────────────────────── */}
        <div className="p-5 md:p-6 border-b border-border space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="font-display font-bold text-lg md:text-xl text-foreground">
                {t(locale, "initiatives")}
              </h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-primary/10 text-primary font-sans text-[10px] tracking-widest tabular-nums">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t(locale, "searchPlaceholder")}
              aria-label={t(locale, "search")}
              leadingIcon={<Search className="w-4 h-4" strokeWidth={1.8} />}
            />
            <FilterSelect
              label={t(locale, "category")}
              value={currentParams.category || "all"}
              options={CATEGORIES.map((c) => ({
                value: c,
                label:
                  c === "all" ? t(locale, "all") : t(locale, c as never) || c,
              }))}
              onChange={(v) =>
                setCategory(v === "all" ? undefined : (v as InitiativeCategory))
              }
            />
            <FilterSelect
              label={t(locale, "status")}
              value={currentParams.status || "all"}
              options={STATUSES.map((s) => ({
                value: s,
                label:
                  s === "all"
                    ? t(locale, "all")
                    : getInitiativeStatus(s, locale),
              }))}
              onChange={(v) =>
                setStatus(v === "all" ? undefined : (v as InitiativeStatus))
              }
            />
          </div>
        </div>

        {/* ─── Body ────────────────────────────────────────────────── */}
        {loading && data.length === 0 ? (
          <div>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} cols={6} />
            ))}
          </div>
        ) : data.length === 0 ? (
          <EmptyTableState locale={locale} />
        ) : (
          <div className="overflow-x-auto relative">
            <table
              className="w-full"
              role="table"
              aria-label={t(locale, "initiatives")}
            >
              <thead className="sticky top-0 bg-white border-b border-border z-10">
                <tr>
                  <Th>{t(locale, "title")}</Th>
                  <Th>{t(locale, "type")}</Th>
                  <Th>{t(locale, "category")}</Th>
                  <Th align="end">{isRTL ? "الميزانية" : "BUDGET"}</Th>
                  <Th>{t(locale, "status")}</Th>
                  <Th align="end">{t(locale, "participants")}</Th>
                  <Th>{t(locale, "startDate")}</Th>
                  <Th>
                    <span className="sr-only">{t(locale, "actions")}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {data.map((initiative) => {
                  const availableActions = getAvailableActions(initiative);
                  return (
                    <tr
                      key={initiative.id}
                      className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors"
                    >
                      <td className="px-5 md:px-6 py-4 max-w-xs">
                        <Link
                          to={`/dashboard/initiatives/${initiative.id}`}
                          className="font-display font-bold text-[14px] text-foreground hover:text-primary transition-colors truncate block"
                        >
                          {getLocalizedTitle(initiative)}
                        </Link>
                      </td>
                      <td className="px-5 md:px-6 py-4 text-[13px] text-foreground-secondary">
                        {getInitiativeType(initiative.type, locale)}
                      </td>
                      <td className="px-5 md:px-6 py-4 text-[13px] text-foreground-secondary">
                        {t(locale, initiative.category as never) ||
                          initiative.category}
                      </td>
                      <td className="px-5 md:px-6 py-4 text-end text-[13px] font-bold tabular-nums text-foreground">
                        {initiative.budget} NC
                      </td>
                      <td className="px-5 md:px-6 py-4">
                        <StatusPill
                          status={initiative.status}
                          locale={locale}
                        />
                      </td>
                      <td className="px-5 md:px-6 py-4 text-end text-[13px] tabular-nums text-foreground-secondary">
                        {initiative.participants || 0}
                      </td>
                      <td className="px-5 md:px-6 py-4 text-[13px] text-foreground-secondary tabular-nums">
                        {formatDate(initiative.start_date)}
                      </td>
                      <td className="px-5 md:px-6 py-4">
                        {availableActions.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              if (openMenuId === initiative.id) {
                                setOpenMenuId(null);
                                setDropdownStyle(null);
                              } else {
                                const r =
                                  e.currentTarget.getBoundingClientRect();
                                const menuH = 100;
                                const top =
                                  window.innerHeight - r.bottom < menuH
                                    ? r.top - menuH - 4
                                    : r.bottom + 4;
                                setDropdownStyle({
                                  position: "fixed",
                                  top: Math.max(8, top),
                                  left: Math.max(8, r.right - 208),
                                  width: 208,
                                  zIndex: 99999,
                                });
                                setOpenMenuId(initiative.id);
                              }
                            }}
                            className="p-1.5 text-foreground-secondary hover:text-primary transition-colors rounded-lg hover:bg-muted"
                            aria-label={t(locale, "actions")}
                            aria-haspopup="menu"
                            aria-expanded={openMenuId === initiative.id}
                          >
                            <MoreVertical
                              className="h-4 w-4"
                              strokeWidth={1.8}
                            />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {loading && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center pointer-events-none">
                <span className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              </div>
            )}
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="px-5 md:px-6 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="font-sans text-[10px] tracking-[0.2em] text-foreground-secondary uppercase tabular-nums">
              {isRTL
                ? `${(meta.current_page - 1) * meta.per_page + 1}–${Math.min(
                    meta.current_page * meta.per_page,
                    meta.total,
                  )} / ${meta.total}`
                : `${(meta.current_page - 1) * meta.per_page + 1}–${Math.min(
                    meta.current_page * meta.per_page,
                    meta.total,
                  )} OF ${meta.total}`}
            </p>
            <div className="flex items-center gap-1">
              <PagerButton
                onClick={() => setPage(meta.current_page - 1)}
                disabled={meta.current_page === 1}
                ariaLabel={isRTL ? "السابق" : "Previous"}
              >
                {isRTL ? (
                  <ChevronRight className="w-4 h-4" strokeWidth={2} />
                ) : (
                  <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                )}
              </PagerButton>
              {paginationPages(meta.current_page, meta.last_page).map(
                (p, idx) =>
                  p === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-2 text-foreground-secondary text-sm"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={cn(
                        "min-w-[36px] h-9 px-2 rounded-full text-[12px] font-bold transition-colors border tabular-nums",
                        meta.current_page === p
                          ? "bg-primary text-white border-primary"
                          : "border-border text-foreground-secondary hover:bg-muted",
                      )}
                    >
                      {p}
                    </button>
                  ),
              )}
              <PagerButton
                onClick={() => setPage(meta.current_page + 1)}
                disabled={meta.current_page === meta.last_page}
                ariaLabel={isRTL ? "التالي" : "Next"}
              >
                {isRTL ? (
                  <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                ) : (
                  <ChevronRight className="w-4 h-4" strokeWidth={2} />
                )}
              </PagerButton>
            </div>
          </div>
        )}
      </div>

      {openInitiative &&
        dropdownStyle &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white dark:bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
            role="menu"
          >
            {openActions.map((action) => (
              <button
                key={action.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpenMenuId(null);
                  setDropdownStyle(null);
                  onAction?.(action.key, openInitiative);
                }}
                className="w-full px-4 py-3 text-[13px] text-foreground hover:bg-muted transition-colors font-medium text-start"
              >
                {action.label}
              </button>
            ))}
            <div className="border-t border-border">
              <Link
                to={`/dashboard/initiatives/${openInitiative.id}`}
                role="menuitem"
                onClick={() => {
                  setOpenMenuId(null);
                  setDropdownStyle(null);
                }}
                className="block w-full px-4 py-3 text-[13px] text-primary hover:bg-muted transition-colors font-medium text-start"
              >
                {t(locale, "viewDetails")}
              </Link>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function Th({
  children,
  align = "start",
}: {
  children: React.ReactNode;
  align?: "start" | "end";
}) {
  return (
    <th
      scope="col"
      className={cn(
        "px-5 md:px-6 py-3 font-sans text-[10px] tracking-[0.15em] text-foreground-secondary uppercase font-medium",
        align === "end" ? "text-end" : "text-start",
      )}
    >
      {children}
    </th>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

function FilterSelect({ label, value, options, onChange }: FilterSelectProps) {
  return (
    <div>
      <label className="block font-sans text-[10px] tracking-widest text-foreground-secondary uppercase mb-2">
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          className={cn(
            "w-full px-4 py-3 rounded-2xl bg-white border outline-none transition-colors appearance-none bg-none",
            "text-[15px] text-foreground",
            "border-neutral-200 hover:border-neutral-300 focus:border-neutral-500",
            "dark:bg-card dark:border-border dark:hover:border-border-hover",
            "pe-10",
          )}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span
          className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-foreground-secondary font-sans text-[10px]"
          aria-hidden
        >
          ▼
        </span>
      </div>
    </div>
  );
}

function StatusPill({ status, locale }: { status: string; locale: Locale }) {
  const live = status === "active" || status === "published";
  const tone = statusTone(status);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide",
        tone.bg,
        tone.text,
      )}
    >
      {live && (
        <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden />
      )}
      <span>{getInitiativeStatus(status, locale)}</span>
    </span>
  );
}

function statusTone(status: string): { bg: string; text: string } {
  switch (status) {
    case "active":
    case "published":
      return { bg: "bg-success/10", text: "text-success" };
    case "draft":
    case "funded":
    case "pending_approval":
    case "incoming":
      return { bg: "bg-amber-500/10", text: "text-amber-700" };
    case "paused":
      return { bg: "bg-amber-500/10", text: "text-amber-700" };
    case "completed":
    case "expired":
      return { bg: "bg-neutral-200", text: "text-foreground-secondary" };
    case "cancelled":
      return { bg: "bg-red-500/10", text: "text-red-600" };
    default:
      return { bg: "bg-muted", text: "text-foreground-secondary" };
  }
}

function EmptyTableState({ locale }: { locale: Locale }) {
  const isRTL = locale === "ar";
  return (
    <div className="p-10 md:p-14 text-center space-y-4">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary">
        <Target className="w-6 h-6" strokeWidth={1.8} />
      </div>
      <h3 className="font-display font-bold text-xl md:text-2xl text-foreground">
        {isRTL ? "لا مبادرات بعد" : "No initiatives yet"}
      </h3>
      <p className="text-[14px] text-foreground-secondary max-w-md mx-auto">
        {isRTL
          ? "حاول تعديل الفلاتر أو ابدأ بمبادرة جديدة."
          : "Try adjusting the filters or start a new initiative."}
      </p>
    </div>
  );
}

function PagerButton({
  onClick,
  disabled,
  ariaLabel,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="p-2 rounded-full border border-border text-foreground-secondary hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );
}

function paginationPages(current: number, last: number): (number | "...")[] {
  const pages: (number | "...")[] = [];
  const range = [1, last, current, current - 1, current + 1].filter(
    (p) => p >= 1 && p <= last,
  );
  const unique = Array.from(new Set(range)).sort((a, b) => a - b);
  unique.forEach((p, idx) => {
    if (idx > 0 && p - (unique[idx - 1] as number) > 1) pages.push("...");
    pages.push(p);
  });
  return pages;
}
